import { useState } from 'react';
import { useTranslation } from '@/src/i18n';
import { LEGAL_URLS } from '@/src/core/config/legalUrls';
import { Button, Input } from '@/src/presentation/components';
import { Ionicons } from '@expo/vector-icons';
import { Image, KeyboardAvoidingView, Linking, Platform, ScrollView, Text, TouchableOpacity, View } from 'react-native';
import { createStyles } from './RegisterScreen.styles';

export interface RegisterScreenProps {
  fullName: string;
  setFullName: (value: string) => void;
  email: string;
  setEmail: (value: string) => void;
  password: string;
  setPassword: (value: string) => void;
  confirmPassword: string;
  setConfirmPassword: (value: string) => void;
  loading: boolean;
  handleRegister: () => void;
  navigateToLogin: () => void;
  colors: {
    background: string;
    text: string;
    textSecondary: string;
    textTertiary: string;
    card: string;
    border: string;
    primary: string;
    primaryText: string;
    error: string;
  };
}

export const RegisterScreen = ({
  fullName,
  setFullName,
  email,
  setEmail,
  password,
  setPassword,
  confirmPassword,
  setConfirmPassword,
  loading,
  handleRegister,
  navigateToLogin,
  colors,
}: RegisterScreenProps) => {
  const styles = createStyles();
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={[styles.container, { backgroundColor: colors.background }]}
    >
      <ScrollView contentContainerStyle={styles.content}>
        <View style={styles.logoContainer}>
          <Image source={require('@/assets/images/android-icon-foreground.png')} style={styles.logoImage} />
        </View>
        <Text style={[styles.title, { color: colors.text }]}>{t('auth.createAccount')}</Text>
        <Text style={[styles.subtitle, { color: colors.textSecondary }]}>{t('auth.joinTournaly')}</Text>

        <View style={styles.form}>
          <Input
            placeholder={t('auth.fullName')}
            value={fullName}
            onChangeText={setFullName}
            editable={!loading}
            colors={colors}
          />

          <Input
            placeholder={t('auth.email')}
            value={email}
            onChangeText={setEmail}
            autoCapitalize="none"
            keyboardType="email-address"
            editable={!loading}
            colors={colors}
          />

          <Input
            placeholder={t('auth.password')}
            value={password}
            onChangeText={setPassword}
            secureTextEntry={!showPassword}
            editable={!loading}
            colors={colors}
            rightElement={
              <TouchableOpacity onPress={() => setShowPassword(v => !v)} disabled={loading}>
                <Ionicons
                  name={showPassword ? 'eye-off-outline' : 'eye-outline'}
                  size={20}
                  color={colors.textTertiary}
                />
              </TouchableOpacity>
            }
          />

          <Input
            placeholder={t('auth.confirmPassword')}
            value={confirmPassword}
            onChangeText={setConfirmPassword}
            secureTextEntry={!showConfirmPassword}
            editable={!loading}
            colors={colors}
            rightElement={
              <TouchableOpacity onPress={() => setShowConfirmPassword(v => !v)} disabled={loading}>
                <Ionicons
                  name={showConfirmPassword ? 'eye-off-outline' : 'eye-outline'}
                  size={20}
                  color={colors.textTertiary}
                />
              </TouchableOpacity>
            }
          />

          <Button
            title={t('auth.signUp')}
            onPress={handleRegister}
            disabled={loading}
            loading={loading}
            colors={colors}
          />

          <View style={styles.legal}>
            <Text style={[styles.legalText, { color: colors.textTertiary }]}>{t('auth.legalPrefix')} </Text>
            <TouchableOpacity onPress={() => Linking.openURL(LEGAL_URLS.termsOfService)}>
              <Text style={[styles.legalLink, { color: colors.primary }]}>{t('settings.termsOfService')}</Text>
            </TouchableOpacity>
            <Text style={[styles.legalText, { color: colors.textTertiary }]}> {t('auth.legalAnd')} </Text>
            <TouchableOpacity onPress={() => Linking.openURL(LEGAL_URLS.privacyPolicy)}>
              <Text style={[styles.legalLink, { color: colors.primary }]}>{t('settings.privacyPolicy')}</Text>
            </TouchableOpacity>
          </View>

          <View style={styles.footer}>
            <Text style={[styles.footerText, { color: colors.textSecondary }]}>{t('auth.alreadyHaveAccount')} </Text>
            <TouchableOpacity onPress={navigateToLogin}>
              <Text style={[styles.footerLink, { color: colors.primary }]}>{t('auth.signIn')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};
